import { TimeOffRequest } from '@/modules/time-off/types/time-off-request';

import { useTimeTrackingStore } from '../../store/time-tracking.store';
import { TimeOffDayCell } from './timeoff-strip/timeoff-day-cell';
import { TimeOffExpandedRow } from './timeoff-strip/timeoff-expanded-row';
import { TimeOffLabelCell } from './timeoff-strip/timeoff-label-cell';
import {
  entriesForSlot,
  pickCellMinHeight,
  sumTimeOffTotals,
} from './timeoff-strip/timeoff-strip.helpers';
import { TimeOffWeeklyTotalCell } from './timeoff-strip/timeoff-weekly-total-cell';
import { WeekSlot } from './week-slot';

interface WeekTimeOffStripProps {
  slots: WeekSlot[];
  weekStart: Date;
  /** Full time-off requests for the week — supply the type / tooltip / expanded detail. */
  requests: TimeOffRequest[];
}

/**
 * Time-off row of the week grid. A thin assembler that emits the row's cells
 * into the parent `.tt-week-grid`: a label cell, one stacked day cell per
 * weekday, a weekly approved/requested total, and the expandable detail panel.
 * Each cell lives in `./timeoff-strip/`; the derivation/layout logic lives in
 * `timeoff-strip.helpers.ts`.
 *
 * Hidden entirely when no weekday has time off. Weekends never render time off,
 * even if the backend returns it.
 */
export function WeekTimeOffStrip({ slots, weekStart, requests }: WeekTimeOffStripProps) {
  const isExpanded = useTimeTrackingStore(state => state.expandedTimeOff);
  const toggle = useTimeTrackingStore(state => state.toggleTimeOffExpanded);

  const totals = sumTimeOffTotals(slots);
  if (totals.approved + totals.requested === 0) return null;

  const cellMinHeightClass = pickCellMinHeight(slots);

  return (
    <>
      <TimeOffLabelCell isExpanded={isExpanded} onToggle={toggle} />
      {slots.map((slot, index) => (
        <TimeOffDayCell
          key={index}
          entries={entriesForSlot(slot)}
          requests={requests}
          date={slot.date}
          minHeightClass={cellMinHeightClass}
          onClick={toggle}
        />
      ))}
      <TimeOffWeeklyTotalCell totals={totals} />
      <TimeOffExpandedRow requests={requests} weekStart={weekStart} isExpanded={isExpanded} />
    </>
  );
}
